import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  TextField,
} from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useDropdowns from 'src/hooks/useDropdowns'
import { AddChapter } from 'src/redux/actions'
import { filterByKeyValue } from 'src/util/helpers'
import * as Yup from 'yup'

import { StatusList } from '../../util/constants'
import DropdownMenu from '../helperComponents/DropdownMenu'
import LoadingButton from '../helperComponents/LoadingButton'

const AddEditChapter = ({
  selectedRow = {},
  toggleDrawer,
  refetch,
  actionTitle,
  setActiveStep,
  firstStepData,
  setFirstStepData
}) => {
  const { courseDifficultyLevel } = useSelector(store => store.mainReducer)
  const initialValues = {
    name: (selectedRow && selectedRow?.name) || '',
    description: (selectedRow && selectedRow?.description) || '',
    course: (selectedRow && selectedRow?.course?._id) || firstStepData?.languageId || '',
    unit: (selectedRow && selectedRow?.unit?._id) || firstStepData?.courseId || '',
    sortOrder: (selectedRow && selectedRow?.sortOrder) || '',
    difficultyLevel:
      (selectedRow && selectedRow?.difficultyLevel) || firstStepData?.difficultyLevel || 'All levels',
    isActive:
      selectedRow && selectedRow.isActive === undefined
        ? true
        : selectedRow && selectedRow?.isActive,
  }
  const dispatch = useDispatch()
  const { dropdowns } = useDropdowns()
  const [totalUnits, setTotalUnits] = useState([])

  useEffect(() => {
    setTotalUnits(dropdowns?.units)
  }, [dropdowns])

  // const isFieldDisabled = !!(isCreateCourse && courseResponse && courseResponse._id);

  const validationSchema = Yup.object().shape({
    name: Yup.string().min(1, 'Minimum 1 letters').max(75, 'Name cannot be more than 75 characters').required('Required'),
    sortOrder: Yup.number().min(0),
    description: Yup.string().min(5, 'Minimum 5 letters').max(200, 'Description cannot be more than 200 characters').required('Required'),
    course: Yup.string().min(5, 'Minimum 5 letters').required('Required'),
    unit: Yup.string().required('Required'),
    sortOrder: Yup.number(),
  })

  const handleCancel = () => {
    toggleDrawer(false)
  }

  const handleSubmit = async values => {
    // same shape as initial values
    const reqData = selectedRow._id
      ? { ...values, _id: selectedRow._id }
      : { ...values }
    const response = await dispatch(AddChapter(reqData));
    if (!response) return;

    switch (actionTitle) {
      case "create-chapter":
        setFirstStepData({
          languageId: response?.course,
          courseId: response?.unit,
          chapterId: response?._id,
          difficultyLevel: response?.difficultyLevel,
        });
        setActiveStep(1);
        break;

      case "create-course":
        setActiveStep(2);
        break;

      default:
        toggleDrawer();
        break;
    }
    refetch();
  }

  const onCourseChange = useCallback(({ target: { value } }, setFieldValue) => {
    setTotalUnits(filterByKeyValue(dropdowns?.units, 'course', value))
    setFieldValue('unit', '')
  }, [dropdowns?.units])

  return (
    <>
      <Box sx={{ m: 1 }}>
        <Container maxWidth="md">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              values,
              handleBlur,
              handleChange,
              setFieldValue,
              handleSubmit,
              isSubmitting,
            }) => (

              <Form>
                <Grid container spacing={2}>
                  {/* LEFT COLUMN */}
                  <Grid item xs={12} md={6} container direction="column" spacing={2}>
                    {/* Name */}
                    <Grid item>
                      <TextField
                        error={Boolean(touched.name && errors.name)}
                        fullWidth
                        helperText={touched.name && errors.name}
                        label="Name"
                        name="name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.name}
                        variant="outlined"
                        required
                        size="small"
                      />
                    </Grid>
                    {/* Description */}
                    <Grid item>
                      <TextField
                        error={Boolean(touched.description && errors.description)}
                        fullWidth
                        helperText={touched.description && errors.description}
                        label="Description"
                        name="description"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.description}
                        variant="outlined"
                        required
                        size="small"
                        multiline
                        rows={4}
                      />
                    </Grid>

                    {/* Course */}
                    <Grid item>
                      <FormControl error={Boolean(touched.course && errors.course)} fullWidth>
                        <DropdownMenu
                          value={values.course}
                          label="Language"
                          name="course"
                          errorMsg={touched.course && errors.course}
                          onChange={(event) => {
                            handleChange(event);
                            onCourseChange(event, setFieldValue);
                          }}
                          data={dropdowns?.courses}
                          isRequired
                        />
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* RIGHT COLUMN */}
                  <Grid item xs={12} md={6} container direction="column" spacing={2}>

                    {/* Course */}
                    <Grid item>
                      <FormControl fullWidth>
                        <DropdownMenu
                          value={values.unit}
                          label="Course"
                          name="unit"
                          onChange={handleChange}
                          data={totalUnits}
                          isRequired
                        />
                      </FormControl>
                    </Grid>

                    {/* Difficulty Level */}
                    <Grid item>
                      <FormControl fullWidth>
                        <DropdownMenu
                          value={values.difficultyLevel}
                          label="Difficulty Level"
                          name="difficultyLevel"
                          onChange={handleChange}
                          data={courseDifficultyLevel}
                        />
                      </FormControl>
                    </Grid>

                    {/* Status */}
                    <Grid item>
                      <FormControl fullWidth>
                        <DropdownMenu
                          value={values.isActive}
                          label="Status"
                          name="isActive"
                          onChange={handleChange}
                          data={StatusList}
                          isRequired
                        />
                      </FormControl>
                    </Grid>

                    {/* Sort Order */}
                    <Grid item>
                      <TextField
                        fullWidth
                        label="Sort Order"
                        inputProps={{ min: 0 }}
                        name="sortOrder"
                        type="number"
                        defaultValue={0}
                        onChange={handleChange}
                        value={values?.sortOrder}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>

                  </Grid>

                  {/* Buttons Row - Full Width */}
                  <Grid item xs={12} container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Button
                        color="primary"
                        onClick={handleCancel}
                        fullWidth
                        size="large"
                        type="button"
                        variant="outlined"
                      >
                        Cancel
                      </Button>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <LoadingButton
                        color="primary"
                        disabled={isSubmitting}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        name="Save"
                      >
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Container>
      </Box>
    </>
  )
}

export default AddEditChapter
